import { logger } from './logger.js';

const LOG_MODULE = 'loggingBootstrap';
const FETCH_LOG_METHOD = 'window.fetch';
const STACKTRACE_LIMIT = 20;
let isInitialized = false;
let isFetchWrapped = false;
let fetchSequence = 0;

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

const getUrlFromFetchInput = (input) => {
  if (typeof input === 'string') {
    return input;
  }
  if (input && typeof input.url === 'string') {
    return input.url;
  }
  return '';
};

const isHeadersLike = (value) => value && typeof value === 'object' && typeof value.forEach === 'function';

const toHeaderObject = (headers) => {
  if (!headers) {
    return {};
  }
  if (Array.isArray(headers)) {
    return headers.reduce((acc, entry) => {
      if (Array.isArray(entry) && entry.length >= 2) {
        acc[String(entry[0]).toLowerCase()] = String(entry[1]);
      }
      return acc;
    }, {});
  }
  if (isHeadersLike(headers)) {
    const result = {};
    headers.forEach((value, key) => {
      result[String(key).toLowerCase()] = String(value);
    });
    return result;
  }
  if (typeof headers === 'object') {
    return Object.entries(headers).reduce((acc, [key, value]) => {
      acc[String(key).toLowerCase()] = String(value);
      return acc;
    }, {});
  }
  return {};
};

const getMethodFromFetchInit = (input, init = {}) => {
  const initMethod = init && typeof init.method === 'string' ? init.method : '';
  if (initMethod) {
    return initMethod.toUpperCase();
  }
  if (input && typeof input.method === 'string' && input.method) {
    return input.method.toUpperCase();
  }
  return 'GET';
};

const normalizeError = (error) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    };
  }
  return {
    value: String(error)
  };
};

const truncateStack = (stack) => {
  if (!stack) {
    return '';
  }
  const lines = String(stack).split('\n');
  return lines.slice(0, STACKTRACE_LIMIT).join('\n');
};

const installGlobalErrorHandlers = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    logger.error(LOG_MODULE, 'Unhandled window error', {
      source: { file: 'loggingBootstrap.js', method: 'window.error' },
      message: event?.message || '',
      filename: event?.filename || '',
      lineno: event?.lineno || null,
      colno: event?.colno || null,
      error: normalizeError(event?.error)
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    logger.error(LOG_MODULE, 'Unhandled promise rejection', {
      source: { file: 'loggingBootstrap.js', method: 'window.unhandledrejection' },
      reason: reason instanceof Error
        ? {
          name: reason.name,
          message: reason.message,
          stack: truncateStack(reason.stack)
        }
        : reason
    });
  });
};

const wrapFetch = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || isFetchWrapped) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  isFetchWrapped = true;

  window.fetch = async (input, init) => {
    const requestId = `fetch-${Date.now()}-${(fetchSequence += 1)}`;
    const startedAt = now();
    const url = getUrlFromFetchInput(input);
    const method = getMethodFromFetchInit(input, init);
    const headers = toHeaderObject((init && init.headers) || (input && input.headers));
    const requestName = headers['x-request-name'] || '';

    logger.debug(LOG_MODULE, 'Network request started', {
      source: { file: 'loggingBootstrap.js', method: FETCH_LOG_METHOD },
      requestId,
      url,
      httpMethod: method,
      requestName
    });

    try {
      const response = await originalFetch(input, init);
      const durationMs = Math.max(0, Math.round(now() - startedAt));
      const context = {
        source: { file: 'loggingBootstrap.js', method: FETCH_LOG_METHOD },
        requestId,
        url,
        httpMethod: method,
        requestName,
        status: response.status,
        ok: response.ok,
        durationMs
      };
      if (response.ok) {
        logger.info(LOG_MODULE, 'Network request completed', context);
      } else {
        logger.warn(LOG_MODULE, 'Network request completed with non-ok status', context);
      }
      return response;
    } catch (error) {
      const durationMs = Math.max(0, Math.round(now() - startedAt));
      logger.error(LOG_MODULE, 'Network request failed', {
        source: { file: 'loggingBootstrap.js', method: FETCH_LOG_METHOD },
        requestId,
        url,
        httpMethod: method,
        requestName,
        durationMs,
        error: normalizeError(error)
      });
      throw error;
    }
  };
};

const installNavigationLogging = () => {
  if (typeof window === 'undefined' || !window.history) {
    return;
  }

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = (...args) => {
    const result = originalPushState(...args);
    logger.info(LOG_MODULE, 'Navigation pushState', {
      source: { file: 'loggingBootstrap.js', method: 'history.pushState' },
      path: String(args[2] || window.location.pathname || '')
    });
    return result;
  };

  window.history.replaceState = (...args) => {
    const result = originalReplaceState(...args);
    logger.info(LOG_MODULE, 'Navigation replaceState', {
      source: { file: 'loggingBootstrap.js', method: 'history.replaceState' },
      path: String(args[2] || window.location.pathname || '')
    });
    return result;
  };

  window.addEventListener('popstate', () => {
    logger.info(LOG_MODULE, 'Navigation popstate', {
      source: { file: 'loggingBootstrap.js', method: 'window.popstate' },
      path: window.location.pathname || '/'
    });
  });
};

export const initializeLogging = () => {
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  logger.info(LOG_MODULE, 'Global logging initialized', {
    source: { file: 'loggingBootstrap.js', method: 'initializeLogging' },
    env: process.env.NODE_ENV || 'development'
  });

  installGlobalErrorHandlers();
  wrapFetch();
  installNavigationLogging();
};

export default initializeLogging;
